import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';
import type { DashboardSummary } from '../crmClient';
import { Callout, Toolbar, formatThaiDate } from '../../components';
import {
  DashboardScreen,
  type ChannelRow, type ClaimOverview, type DashboardPeriod, type MemberOverview, type PeriodFlow,
  type PeriodPreset, type PointOverview, type Section,
} from '../../screens/DashboardScreen';
import { SLA_HOURS } from '../../screens/mockData';

/**
 * ต่อหน้าภาพรวมเข้ากับ CRM module
 *
 *   ณ ตอนนี้      ← endpoint เดิม (point-claim · loyalty-account · loyalty-tier-summary · loyalty-point-summary)
 *   ในช่วงที่เลือก ← `dashboard-summary/` (CRM 9cfe054)
 *
 * แยกสองทางโดยตั้งใจ — ถ้า host ยังไม่มี getDashboardSummary หรือเรียกแล้วล้ม
 * ตัวเลข ณ ตอนนี้ยังขึ้นได้ครบ ไม่ตายไปพร้อมกัน
 *
 * ⚠️ ห่อทุกการเรียกด้วย Promise.resolve().then — host ที่ deploy แยกจากโมดูลอาจยังไม่มีบางเมธอด
 *    เรียกตรง ๆ จะ throw ก่อนถึง .catch แล้วทั้งหน้าขาว (เจอจริงกับ listPackages)
 */

/** จำนวนคำขอรอตรวจที่ดึงมานับงานเกิน SLA — เรียงเก่าสุดก่อน งานเกิน SLA จึงอยู่ในชุดนี้ก่อนเสมอ */
const PENDING_SAMPLE = 500;
const EXPIRE_DAYS = 90;
/** backend ให้รายวันได้ไม่เกิน 92 วัน — ยาวกว่านั้นสลับเป็นรายเดือนให้เอง */
const MAX_DAYS = 92;
/** พิมพ์วันที่ในช่องกำหนดเองทีละตัว ไม่ควรยิงคำขอทุกครั้งที่กด */
const CUSTOM_DEBOUNCE_MS = 400;

const CHANNEL_LABEL: Record<string, string> = {
  RECEIPT: 'ใบเสร็จ / สลิป',
  BANK_TRANSFER: 'โอนเงิน',
  OTHER: 'ช่องทางอื่น',
  PLATFORM: 'ร้านค้าออนไลน์',
  SHOPEE: 'Shopee',
  LAZADA: 'Lazada',
  TIKTOK: 'TikTok Shop',
};

/* ------------------------------------------------------------- ช่วงเวลา */

/** วันที่ตามเครื่องผู้ใช้ (เวลาไทย) — ไม่ใช้ toISOString เพราะเป็น UTC จะเลื่อนวันตอนเช้ามืด */
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const daysBetween = (from: string, to: string) =>
  Math.round((new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86_400_000) + 1;

function periodOf(preset: PeriodPreset, custom?: { from: string; to: string }): DashboardPeriod {
  const today = new Date();
  const back = (days: number) => { const d = new Date(today); d.setDate(d.getDate() - days); return ymd(d); };
  switch (preset) {
    case 'last7': return { preset, from: back(6), to: ymd(today), interval: 'day' };
    case 'thisMonth': return { preset, from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to: ymd(today), interval: 'day' };
    case 'last12m': return { preset, from: ymd(new Date(today.getFullYear(), today.getMonth() - 11, 1)), to: ymd(today), interval: 'month' };
    case 'custom': {
      const from = custom?.from ?? back(29);
      const to = custom?.to ?? ymd(today);
      return { preset, from, to, interval: daysBetween(from, to) > MAX_DAYS ? 'month' : 'day' };
    }
    case 'last30':
    default:
      return { preset: 'last30', from: back(29), to: ymd(today), interval: 'day' };
  }
}

/* ------------------------------------------------------------- แปลงข้อมูล */

const n = (v: string | number | null | undefined) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

function axisLabels(dates: string[], interval: 'day' | 'month') {
  const parse = (iso: string) => new Date(`${iso}T00:00:00`);
  return {
    labels: dates.map((d) => parse(d).toLocaleDateString('th-TH', interval === 'day'
      ? { day: 'numeric', month: 'short' }
      : { month: 'short', year: '2-digit' })),
    fullLabels: dates.map((d) => (interval === 'day'
      ? formatThaiDate(d)
      : parse(d).toLocaleDateString('th-TH', { month: 'long', year: 'numeric' }))),
  };
}

/** ทุก series เรียงตามวันที่ของช่วงเดียวกัน — map ตามวันที่ ไม่อิงลำดับ เผื่อหมวดไหนขาดวัน */
function toFlow(s: DashboardSummary): PeriodFlow {
  const dates = (s.claims?.series ?? s.orders?.series ?? s.points?.series ?? s.members?.series ?? []).map((r) => r.date);
  const pick = <R extends { date: string }>(rows: R[] | undefined, get: (r: R) => number) => {
    const byDate = new Map((rows ?? []).map((r) => [r.date, get(r)]));
    return dates.map((d) => byDate.get(d) ?? 0);
  };
  const { labels, fullLabels } = axisLabels(dates, s.period.interval);

  const channels: ChannelRow[] = (s.claims?.byChannel ?? []).map((c) => ({
    label: (c.platform && CHANNEL_LABEL[c.platform]) || CHANNEL_LABEL[c.type] || c.type,
    submitted: c.submitted,
    approved: c.approved,
    rejected: c.rejected,
    approvedAmount: n(c.approvedAmount),
  }));

  const o = s.orders?.inPeriod;
  const p = s.points?.inPeriod;
  const c = s.claims?.inPeriod;
  return {
    labels,
    fullLabels,
    members: {
      newInPeriod: s.members?.newInPeriod ?? 0,
      unknownJoinDate: s.members?.unknownJoinDate ?? 0,
      newSeries: pick(s.members?.series, (r) => r.new),
    },
    points: {
      earned: n(p?.earned), redeemed: n(p?.redeemed), expired: n(p?.expired),
      adjustedAdd: n(p?.adjustedAdd), adjustedDeduct: n(p?.adjustedDeduct),
      earnedSeries: pick(s.points?.series, (r) => n(r.earned)),
      redeemedSeries: pick(s.points?.series, (r) => n(r.redeemed)),
    },
    claims: {
      submitted: c?.submitted ?? 0, approved: c?.approved ?? 0, rejected: c?.rejected ?? 0,
      approvedAmount: n(c?.approvedAmount), avgReviewHours: c?.avgReviewHours ?? null,
      channels, submittedSeries: pick(s.claims?.series, (r) => r.submitted),
    },
    orders: {
      count: o?.count ?? 0, gross: n(o?.gross), discount: n(o?.discount), net: n(o?.net), refunded: n(o?.refunded),
      canceledCount: o?.canceledCount ?? 0, avgTicket: n(o?.avgTicket), memberCount: o?.memberCount ?? 0,
      memberNet: n(o?.memberNet), netSeries: pick(s.orders?.series, (r) => n(r.net)),
    },
  };
}

const hoursSince = (iso: string, now: number) => (now - new Date(iso).getTime()) / 3_600_000;

/* ---------------------------------------------------------------- container */

export function DashboardContainer() {
  const { api, dataMode, linkComponent } = useCRMHost();
  const isMock = dataMode === 'mock';

  /* ── ณ ตอนนี้ ── */
  const [claims, setClaims] = useState<Section<ClaimOverview>>({ state: 'loading' });
  const [members, setMembers] = useState<Section<MemberOverview>>({ state: 'loading' });
  const [points, setPoints] = useState<Section<PointOverview>>({ state: 'loading' });
  const [updatedAt, setUpdatedAt] = useState<string | undefined>(undefined);
  const [refreshing, setRefreshing] = useState(false);

  /* ── ในช่วงที่เลือก ── */
  const [period, setPeriod] = useState<DashboardPeriod>(() => periodOf('last30'));
  const [flow, setFlow] = useState<Section<PeriodFlow> | { state: 'unavailable' }>({ state: 'loading' });
  const [flowRefreshing, setFlowRefreshing] = useState(false);
  const [flowError, setFlowError] = useState<string | undefined>(undefined);
  /** คำขอรอตรวจของใบเสร็จที่ถูกยกเลิก — มีเฉพาะใน dashboard-summary */
  const [pendingCanceled, setPendingCanceled] = useState<number | undefined>(undefined);

  /** นับรอบ — คำตอบของรอบเก่าที่มาช้าต้องถูกทิ้ง */
  const nowRun = useRef(0);
  const flowRun = useRef(0);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadNow = useCallback(async () => {
    const mine = (nowRun.current += 1);
    const alive = () => mine === nowRun.current;
    setRefreshing(true);

    const claimTask = Promise.resolve()
      .then(() => api.listClaims({ status: 'PENDING', ordering: 'created', limit: PENDING_SAMPLE }))
      .then((pending) => {
        if (!alive()) return;
        const now = Date.now();
        const ages = pending.items.map((c) => hoursSince(c.created, now));
        const overdue = ages.filter((h) => h > SLA_HOURS).length;
        setClaims({
          state: 'ready',
          data: {
            pending: pending.total,
            overdue,
            /* ทุกคำขอในชุดที่ดึงมาเกิน SLA และยังมีอีก → ตัวจริงมากกว่าที่นับได้ */
            overdueIsLowerBound: pending.total > pending.items.length && overdue === pending.items.length,
            oldestPendingHours: ages.length ? Math.max(...ages) : null,
            slaHours: SLA_HOURS,
          },
        });
      })
      .catch(() => { if (alive()) setClaims({ state: 'error' }); });

    const memberTask = Promise.resolve()
      .then(() => Promise.all([api.listAccounts(1), api.listTierSummary()]))
      .then(([accounts, tiers]) => {
        if (!alive()) return;
        setMembers({
          state: 'ready',
          data: {
            total: accounts.total,
            byTier: [...tiers]
              .sort((a, b) => Number(a.tier.startPoint) - Number(b.tier.startPoint))
              .map((t) => ({ name: t.tier.name, count: t.totalAccounts })),
          },
        });
      })
      .catch(() => { if (alive()) setMembers({ state: 'error' }); });

    const pointTask = Promise.resolve()
      .then(() => api.getPointSummary(EXPIRE_DAYS))
      .then((s) => {
        if (!alive()) return;
        setPoints({
          state: 'ready',
          data: {
            outstanding: n(s.totalPoints),
            accounts: s.totalAccounts,
            expiring: n(s.expiringPoints),
            expireDays: s.expireDays ?? EXPIRE_DAYS,
          },
        });
      })
      .catch(() => { if (alive()) setPoints({ state: 'error' }); });

    await Promise.all([claimTask, memberTask, pointTask]);
    if (alive()) {
      setUpdatedAt(new Date().toISOString());
      setRefreshing(false);
    }
  }, [api]);

  const loadFlow = useCallback(async (p: DashboardPeriod) => {
    const mine = (flowRun.current += 1);
    const alive = () => mine === flowRun.current;

    /* host ที่ deploy ก่อนโมดูลยังไม่มีเมธอดนี้ — บอกตรง ๆ ไม่ถือเป็น error */
    if (typeof api.getDashboardSummary !== 'function') {
      setFlow({ state: 'unavailable' });
      return;
    }
    /* มีข้อมูลเดิมอยู่แล้ว → คงกรอบไว้แบบจาง ไม่กระพริบเป็นโครงว่าง */
    setFlow((prev) => (prev.state === 'ready' ? prev : { state: 'loading' }));
    setFlowRefreshing(true);
    setFlowError(undefined);
    try {
      const s = await api.getDashboardSummary({ dateFrom: p.from, dateTo: p.to, interval: p.interval });
      if (!alive()) return;
      setFlow({ state: 'ready', data: toFlow(s) });
      setPendingCanceled(s.claims?.pendingCanceledReceipt);
    } catch (err) {
      if (!alive()) return;
      setFlow({ state: 'error' });
      /* ข้อความจาก backend เป็นภาษาไทยอยู่แล้ว เช่น "ข้อมูลรายเดือนดูได้ไม่เกิน 24 เดือน" */
      setFlowError(err instanceof Error ? err.message : undefined);
    } finally {
      if (alive()) setFlowRefreshing(false);
    }
  }, [api]);

  useEffect(() => {
    if (isMock) return;
    void loadNow();
  }, [isMock, loadNow]);

  useEffect(() => {
    if (isMock) return;
    void loadFlow(period);
  }, [isMock, loadFlow, period]);

  useEffect(() => () => { if (debounce.current) clearTimeout(debounce.current); }, []);

  const onPeriodChange = useCallback((preset: PeriodPreset, custom?: { from: string; to: string }) => {
    if (debounce.current) clearTimeout(debounce.current);
    if (preset !== 'custom') { setPeriod(periodOf(preset)); return; }
    if (!custom || custom.from > custom.to) return;
    debounce.current = setTimeout(() => setPeriod(periodOf('custom', custom)), CUSTOM_DEBOUNCE_MS);
  }, []);

  const onRefresh = useCallback(() => {
    void loadNow();
    void loadFlow(period);
  }, [loadNow, loadFlow, period]);

  if (isMock) {
    return (
      <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-100)] font-[family-name:var(--font-thai)]">
        <Toolbar title="ภาพรวม" />
        <div className="p-[var(--space-4)]">
          <Callout tone="info" title="หน้านี้ใช้กับข้อมูลจริงเท่านั้น">
            ตัวเลขภาพรวมต้องต่อกับระบบจริง โหมดสาธิตไม่มีข้อมูลสรุปให้แสดง
          </Callout>
        </div>
      </div>
    );
  }

  const claimSection: Section<ClaimOverview> = claims.state === 'ready'
    ? { state: 'ready', data: { ...claims.data, pendingCanceledReceipt: pendingCanceled } }
    : claims;

  return (
    <DashboardScreen
      claims={claimSection}
      members={members}
      points={points}
      period={period}
      onPeriodChange={onPeriodChange}
      flow={flow}
      flowRefreshing={flowRefreshing && flow.state === 'ready'}
      flowError={flowError}
      updatedAt={updatedAt}
      refreshing={refreshing}
      onRefresh={onRefresh}
      linkComponent={linkComponent}
    />
  );
}
